import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Typography,
  Card,
  Row,
  Col,
  Tabs,
  Tag,
  Descriptions,
  Table,
  Space,
  Progress,
  Alert,
  Statistic,
  Divider,
  message,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";

import { canViewProject, getCurrentUserRole } from "../../config/rbac";
import { toMmDdYyyy } from "../../utils/dateFormat";
import { isMasked, sumAmounts, safeCalc } from "../../utils/maskedValue";

import ReviewCommentsAndActionItemsView from "./partials/ReviewCommentsAndActionItemsView";
import LeadershipAttentionReadOnlyView from "./partials/LeadershipAttentionReadOnlyView";
import ValueAddsReadOnlyView from "./partials/ValueAddsReadOnlyView";
import  ProjectHealthStatus  from "../../components/ProjectHealthStatus";
import MarkdownText from "../../components/MarkdownText";
import DataState from "../../components/DataState/DataState";
import { useProjectDetailsQuery } from "./projectDetails.hooks";
import { ProjectNotFoundError } from "../../services/projectDetailsApi";

const { Title, Text, Paragraph } = Typography;

const statusColors = {
  Invoiced: "green",
  Inactive: "orange",
  "On Hold": "orange",
  Completed: "blue",
  "At Risk": "red",
  Pending: "default",
  Overdue: "error",
  Open: "orange",
  Closed: "green",
  Mitigated: "blue",
  "In Progress": "processing",
  Yes: "success",
  No: "error",
};

const getStatusBadgeStyle = (status) => {
  const isActive = status === "Active";
  const borderColor = isActive ? "#52c41a" : "#ff0000";
  const textColor = isActive ? "#389e0d" : "#cf1322";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 92,
    padding: "4px 12px",
    borderRadius: 8,
    border: `1px solid ${borderColor}`,
    background: "#ffffff",
    color: textColor,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  };
};

const currencySymbols = {
  USD: "$",
  INR: "INR ",
  EUR: "EUR ",
  GBP: "GBP ",
};

function formatCurrency(rawVal) {
  if (isMasked(rawVal)) return rawVal.trim();
  const val = Number(rawVal) || 0;
  // Currency display is fixed to USD on the project details page.
  const symbol = currencySymbols.USD;

  if (val < 100_000) {
    return `${symbol}${val.toLocaleString()}`;
  }

  if (val < 1_000_000) {
    return `${symbol}${(val / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return `${symbol}${(val / 1_000_000).toFixed(2)}M`;
}
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const raw = String(dateStr).trim();
  if (!raw || raw === "N/A") return "-";

  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const [, year, month, day] = ymd;
    return `${month}-${day}-${year}`;
  }

  const dashDate = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dashDate) {
    const [, first, second, year] = dashDate;
    const firstNum = Number(first);
    const secondNum = Number(second);
    if (firstNum > 12 && secondNum <= 12) {
      return `${second}-${first}-${year}`;
    }
    return `${first}-${second}-${year}`;
  }

  const mdy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (mdy) {
    const [, month, day, year] = mdy;
    return `${month}-${day}-${year}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${month}-${day}-${year}`;
  }

  return raw;
};

export function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentRole = getCurrentUserRole();
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!canViewProject(currentRole)) {
      message.error("You do not have permission to view project details");
      navigate("/watchtower/projects");
    }
  }, [currentRole, navigate]);

  useEffect(() => {
    if (!id) {
      message.error("No project selected");
      navigate("/watchtower/projects");
    }
  }, [id, navigate]);

  const { data: project, isLoading, isFetching, isError, error, refetch } = useProjectDetailsQuery(id);

  if (!id) {
    return null;
  }

  // 'loading' while the query is in flight (including retry and project change), so no old details show.
  const detailsStatus = isLoading || isFetching ? "loading" : isError || !project ? "error" : "ready";
  const dataStateProps = {
    status: detailsStatus,
    errorTitle: error instanceof ProjectNotFoundError ? "Project not found" : "Couldn't load project",
    error: error instanceof Error ? error.message : "The project you're looking for doesn't exist.",
    onRetry: () => refetch(),
    skeletonRows: 6,
  };

  if (detailsStatus !== "ready") {
    return <DataState {...dataStateProps} />;
  }

  const allMilestones = project.sows.flatMap((sow) =>
    sow.milestones.map((m) => ({
      ...m,
      sowTitle: sow.title,
      sowNumber: sow.sowNumber,
    })),
  );

  const totalContractValue = sumAmounts(project.sows.map((sow) => sow.contractValue));
  const approvedInvoices = project.invoices.filter((invoice) => invoice.status?.toUpperCase() === "INVOICED");
  const pendingInvoices = project.invoices.filter((invoice) => invoice.status?.toUpperCase() === "PENDING APPROVAL");
  const rejectedInvoices = project.invoices.filter((invoice) => invoice.status?.toUpperCase() === "REJECTED");

  const totalInvoiced = sumAmounts(approvedInvoices.map((invoice) => invoice.amount));
  const totalSubmittedInvoiceAmount = sumAmounts(project.invoices.map((invoice) => invoice.amount));
  const remaining = safeCalc([totalContractValue, totalInvoiced], (tcv, inv) => Math.max(tcv - inv, 0));

  const completedMilestones = allMilestones.filter((milestone) => milestone.status === "Paid").length;
  const progressPct = allMilestones.length ? Math.round((completedMilestones / allMilestones.length) * 100) : 0;
  // Percentages can't be derived from masked values -> fall back to 0
  const invoiceRealizationPct = safeCalc(
    [totalContractValue, totalInvoiced],
    (tcv, inv) => (tcv > 0 ? Math.min(100, Math.round((inv / tcv) * 100)) : 0),
    0,
  );
  const invoiceApprovalPct = safeCalc(
    [totalSubmittedInvoiceAmount, totalInvoiced],
    (sub, inv) => (sub > 0 ? Math.round((inv / sub) * 100) : 0),
    0,
  );

  const milestoneColumns = [
    { title: "Milestone Id", dataIndex: "id", key: "id", width: 90 },
    { title: "Milestone", dataIndex: "name", key: "name", width: 260 },
    { title: "Milestone Start Date", dataIndex: "dueDate", key: "dueDate", width: 110, render: (v) => formatDate(v) },
    { title: "Milestone End Date", dataIndex: "completionDate", key: "completionDate", width: 110, render: (v) => formatDate(v) },
    { title: `Amount (USD)`, dataIndex: "paymentAmount", key: "paymentAmount", width: 120, render: (v) => <Text strong>{formatCurrency(v)}</Text> },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (v) => <Tag color={statusColors[String(v || "")] || "default"}>{String(v || "-")}</Tag>,
    },
  ];

  const invoiceColumns = [
    { title: "Invoice Id", dataIndex: "invoiceNumber", key: "invoiceNumber" },
    { title: "Revenue Month", dataIndex: "milestoneName", key: "milestoneName" },
    { title: "Invoice Submitted Date", dataIndex: "invoiceDate", key: "invoiceDate", render: (v) => toMmDdYyyy(v, "-") },
    { title: `Amount (USD)`, dataIndex: "amount", key: "amount", render: (v) => <Text strong style={{ color: "#1677ff" }}>{formatCurrency(v)}</Text> },
    {
      title: "FieldGlass Status", dataIndex: "status", key: "status", render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={v === "Invoiced"||v==="INVOICED" ? "success" : v === "Pending Approval" || "PENDING APPROVAL"? "warning" : "error"}>{v}</Tag>
          {(v === "Pending Approval" || (v === "Rejected" && r.reason)) && <Text style={{ fontSize: 11, color: "#8c8c8c" }}>{r.reason}</Text>}
        </Space>
      ),
    },
  ];

  const actionColumns = [
    { title: "Action Item", dataIndex: "description", key: "description", width: 260 },
    { title: "Owner", dataIndex: "owner", key: "owner", width: 130 },
    { title: "Due Date", dataIndex: "dueDate", key: "dueDate", width: 110 },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 90, render: (v) => <Tag color={v === "High" ? "error" : v === "Medium" ? "warning" : "default"}>{v}</Tag> },
    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (v) => <Tag color={statusColors[v] || "default"}>{v}</Tag> },
  ];

  return (
    <DataState {...dataStateProps}>
    <div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Tag color="blue">{project.projectId}</Tag>
          {/* <Tag color="default">SOW: {project.sows[0]?.sowNumber || "N/A"}</Tag> */}
          {/* {project.redFlagged && <Tag icon={<WarningOutlined />} color="error">Inactive</Tag>} */}
          {/* <EditOutlined style={{fontSize:"23px", marginLeft: "auto",cursor: "pointer",}} onClick={() => navigate(`/projects/${project.id}/edit`)} /> */}

        </div>
        <Title level={4} style={{ margin: "4px 0 0" }}>{project.name}</Title>
        <Text style={{ color: "#8c8c8c" }}>{project.client}</Text>

      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={6}>
          <Card style={{ borderRadius: 12, textAlign: "center", border: "1px solid #f0f0f0" }}>
            <Statistic
              title="Total Contract Value"
              value={formatCurrency(totalContractValue)}
              style={{ fontSize: 20, color: "#52c41a" }}
            />          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ borderRadius: 12, textAlign: "center", border: "1px solid #f0f0f0" }}>
            <Statistic title="Total Invoiced Till Date" value={formatCurrency(totalInvoiced)} valueStyle={{ fontSize: 20, color: "#1677ff" }} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ borderRadius: 12, textAlign: "center", border: "1px solid #f0f0f0" }}>
            <Statistic title="Pending Invoice Count" value={pendingInvoices.length} valueStyle={{ fontSize: 20, color: "#FABB02" }} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={{ borderRadius: 12, textAlign: "center", border: "1px solid #f0f0f0" }}>
            <Statistic title="Rejected Invoice Count" value={rejectedInvoices.length} valueStyle={{ fontSize: 20, color: "#cf1322" }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ borderRadius: 12, border: "1px solid #f0f0f0" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: "overview",
            label: "Overview",
            children: (
              <div>
                <Row gutter={24}>
                  <Col xs={24} md={17}>
                    <Descriptions
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            width: "100%",
                          }}
                        >
                          <Text strong style={{ fontSize: 16 }}>
                            Project Information
                          </Text>
                          <span
                            aria-label={`Project status: ${project.status}`}
                            style={getStatusBadgeStyle(project.status)}
                          >
                            {project.status}
                          </span>
                        </div>
                      }
                      bordered
                      size="small"
                      column={2}
                    >
                      <Descriptions.Item label="Project ID" span={2} style={{ width: 135 }}>{project.projectId}</Descriptions.Item>
                      <Descriptions.Item label="SOW ID" span={2} style={{ width: 135 }}>{project.sows[0]?.sowNumber || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Customer Name" span={2} style={{ width: 135 }}>{project.client}</Descriptions.Item>
                      <Descriptions.Item label="Project Name" span={2} style={{ width: 135 }} ><Paragraph style={{ margin: 0 }}>{project.name}</Paragraph></Descriptions.Item>
                      <Descriptions.Item label="Client Partner" style={{ width: 135 }} >{project.cp?.join(", ") || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Delivery Manager" style={{ width: 135 }} >{project.dm?.join(", ") || project.deliveryManager}</Descriptions.Item>
                      <Descriptions.Item label="Project Manager" style={{ width: 135 }} >{project.pm?.join(", ") || project.projectManager}</Descriptions.Item>
                      {/* <Descriptions.Item label="Status" style={{width: 135}} >
                        <Tag color={statusColors[project.status] || "default"}>{project.status}</Tag>
                      </Descriptions.Item> */}
                      <Descriptions.Item label="PCR ID" style={{ width: 135 }}>{project.pcrIds?.join(", ") || "N/A"}</Descriptions.Item>
                      <Descriptions.Item label="Project Start Date" style={{ width: 135 }}>{formatDate(project.startDate)}</Descriptions.Item>
                      <Descriptions.Item label="Project End Date" style={{ width: 135 }}>{formatDate(project.endDate)}</Descriptions.Item>
                      {/* <Descriptions.Item label="Project ID" span={2} style={{width: 135}}>{project.projectId}</Descriptions.Item> */}
                      <Descriptions.Item label="Business Area" span={2} style={{ width: 135 }}>{project.businessArea?.join(", ") || "N/A"}</Descriptions.Item>

                      <Descriptions.Item label="Scope" span={2} style={{ width: 135 }}><MarkdownText text={project.sows[0]?.scope} /></Descriptions.Item>
                      <Descriptions.Item label="Technology" span={2} style={{ width: 135 }}><MarkdownText text={project.sows[0]?.technology} /></Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={7}>
                    <div style={{ marginBottom: 20, textAlign: "center" }}>
                      <Text style={{ fontWeight: 600, display: "block", marginBottom: 12 }}>Milestone Progress</Text>


                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <Progress type="circle" percent={progressPct} size={120} strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }} />
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <Text style={{ color: "#8c8c8c" }}>{completedMilestones} of {allMilestones.length} milestones completed</Text>
                      </div>
                    </div>
                    <Divider />
                    <div>
                      <Text style={{ fontWeight: 600, display: "block", marginBottom: 12 }}>Financial Summary</Text>
                      <Row gutter={[16, 16]}>
                        {[
                          { label: "GPM %", planned: project.plannedGpmPercentage, actual: project.actualGpmPercentage },
                          { label: "EBITDA %", planned: project.plannedEbitdaPercentage, actual: project.actualEbitdaPercentage },
                        ].map((row) => {
                          const plannedValue = Number(row.planned ?? 0);
                          const actualValue = Number(row.actual ?? 0);
                          const variance = actualValue - plannedValue;
                          const isPositive = variance >= 0;
                          return (
                            <Col xs={24} key={row.label}>
                              <div
                                style={{
                                  padding: "10px 12px",
                                  borderRadius: 8,
                                  border: "1px solid #f0f0f0",
                                  background: "#fafafa",
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                  <Text style={{ fontSize: 12, color: "#8c8c8c" }}>{row.label}</Text>
                                  <Tag color={isPositive ? "success" : "error"}>
                                    {isPositive ? "+" : ""}
                                    {variance.toFixed(1)}%
                                  </Tag>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                                  <div>
                                    <Text style={{ fontSize: 11, color: "#8c8c8c" }}>Planned</Text>
                                    <div>
                                      <Text strong>{plannedValue.toFixed(1)}%</Text>
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <Text style={{ fontSize: 11, color: "#8c8c8c" }}>Actual</Text>
                                    <div>
                                      <Text strong style={{ color: isPositive ? "#389e0d" : "#cf1322" }}>
                                        {actualValue.toFixed(1)}%
                                      </Text>
                                    </div>
                                  </div>
                                </div>
                                <Progress
                                  percent={Math.min(100, Math.max(0, actualValue))}
                                  size="small"
                                  showInfo={false}
                                  strokeColor={isPositive ? "#52c41a" : "#ff0000"}
                                />
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>
            ),
          },
          {
            key: "sows",
            label: `SOW (${project.sows.length})`,
            children: (
              <div>{project.sows.length === 0 ? <Alert type="info" message="No SOW data available for this project." /> : project.sows.map((sow) => (
                <Card key={sow.id} style={{ marginBottom: 16, borderRadius: 8, border: "1px solid #f0f0f0" }} title={<Space><FileTextOutlined /><Tag>{sow.sowNumber}</Tag></Space>}>
                  <div>
                    {[
                      { label: "Total Contract Value", value: <Text strong style={{ color: "#1677ff" }}>{formatCurrency(sow.contractValue)}</Text> },
                      { label: "Project Scope", value: <MarkdownText text={sow.scope} /> },
                      { label: "Technology", value: <MarkdownText text={sow.technology} /> },
                      { label: "Business Area", value: project.businessArea?.join(", ") || "N/A" },
                    ].map((item) => (
                      <div key={item.label} style={{ display: "flex", alignItems: "flex-start", padding: "6px 0" }}>
                        <Text style={{ width: 160, flexShrink: 0, color: "#8c8c8c" }}>{item.label}</Text>
                        <div style={{ flex: 1 }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                  <Divider>Milestones ({sow.milestones.length})</Divider>
                  <Table columns={milestoneColumns} dataSource={sow.milestones} rowKey="id" size="small" pagination={false} scroll={{ x: 700 }} />
                </Card>
              ))}</div>
            ),
          },
          {
            key: "invoices",
            label: `Invoices (${project.invoices.length})`,
            children: (
              <div>
                {project.invoices.length === 0 ? <Alert type="info" message="No invoices available." /> : <Table columns={invoiceColumns} dataSource={project.invoices} rowKey="id" size="small" scroll={{ x: 700 }} pagination={false} />}
              </div>
            ),
          },
          {
            key: "risks",
            label: `Leadership Attention`,
            children: (
              <div>
                <LeadershipAttentionReadOnlyView projectId={project.projectId} />
              </div>
            ),
          },
          {
            key: "review-actions",
            label: `Review & Actions`,
            children: (
              <div>
  
                <ReviewCommentsAndActionItemsView
                  projectId={project.projectId}
                  readOnly
                />
              </div>
            ),
          },
          {
            key: "value-adds",
            label: `Value Adds`,
            children: (
              <div>
                <ValueAddsReadOnlyView projectId={project.projectId} />
              </div>
            ),
          },
          {
            key: "reports",
            label: `Status Reports `,
            children: (
              <div>
                <ProjectHealthStatus projectId={project.projectId} readOnly />
                <div style={{ marginTop: 16 }}>
                </div>
              </div>
            ),
          },
        ]} />
      </Card>
    </div>
    </DataState>
  );
}

